/**
 * useUiTheme — Derives the UI glass theme from the active 3D scene.
 *
 * Some scenes (e.g. 'night') have a near-black background. In those cases
 * the default dark-glass UI (black-tinted blur) becomes invisible. This hook
 * returns Tailwind class strings and a flag so components can switch to a
 * light-glass variant (white-tinted blur) automatically.
 *
 * Usage:
 *   const theme = useUiTheme();
 *   <div className={theme.pill} />
 *   <button className={isActive ? theme.btnActive : theme.btnInactive} />
 */
import { useAppStore } from '../store/app.store';

/** Scene IDs whose background is dark enough to need a light-glass UI. */
const DARK_BG_SCENES = new Set(['night']);

export interface UiTheme {
  /** True when the 3D scene background is very dark → UI uses light glass. */
  isDark: boolean;
  /** Tailwind bg class for the MenuBar pill container. */
  pill: string;
  /** Tailwind bg+hover classes for an inactive MenuButton. */
  btnInactive: string;
  /** Tailwind bg class for an active MenuButton. */
  btnActive: string;
  /** CSS gradient string for the SubMenuPanel backdrop. */
  panelGradient: string;
  /** Active tab inside a pill (CameraSwitchPill, SceneSubMenu road pill). */
  tabActive: string;
  /** Hover class for an inactive tab inside a pill. */
  tabInactive: string;
  /** GlassIconButton (Return, AR) base bg. */
  glassBtn: string;
  /** GlassIconButton hover bg — applied when NOT pressed. */
  glassBtnHover: string;
  /** GlassIconButton pressed bg. */
  glassBtnPressed: string;
}

export function useUiTheme(): UiTheme {
  const activeScene  = useAppStore((s) => s.activeScene);
  const cameraMode   = useAppStore((s) => s.cameraMode);
  // Interior always uses the light-glass variant (white-tinted), regardless of scene.
  const isDark = cameraMode === 'interior' || DARK_BG_SCENES.has(activeScene);

  if (isDark) {
    return {
      isDark: true,
      // Light-glass: white tint so pill is visible on dark backgrounds
      pill:            'bg-white/30',
      btnInactive:     'bg-black/50 hover:bg-white/30',
      btnActive:       'bg-black',
      tabActive:       'bg-black',
      tabInactive:     'hover:bg-white/20',
      glassBtn:        'bg-white/30',
      glassBtnHover:   'hover:bg-white/35',
      glassBtnPressed: 'bg-white/50',
      panelGradient:
        'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.68) 15%, rgba(0,0,0,0.54) 30%, rgba(0,0,0,0.37) 50%, rgba(0,0,0,0.18) 70%, rgba(0,0,0,0.05) 87%, transparent 100%)',
    };
  }

  return {
    isDark: false,
    // Dark-glass default
    pill:            'bg-black/40',
    btnInactive:     'bg-black/50 hover:bg-black',
    btnActive:       'bg-black',
    tabActive:       'bg-black/60',
    tabInactive:     'hover:bg-black/30',
    glassBtn:        'bg-black/55',
    glassBtnHover:   'hover:bg-black/80',
    glassBtnPressed: 'bg-black',
    panelGradient:
      'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.68) 15%, rgba(0,0,0,0.54) 30%, rgba(0,0,0,0.37) 50%, rgba(0,0,0,0.18) 70%, rgba(0,0,0,0.05) 87%, transparent 100%)',
  };
}
